import React, { CSSProperties, useEffect, useState, useCallback } from 'react';

import isEqual from 'lodash-es/isEqual';

import Select, { StylesConfig } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';

import { parseFilterPathParam } from 'src/components/pipelines/utils/pipeline-filter-query';

import {
  FiltersProps,
  PipelineFilter,
  PipelineType,
  StateProps,
  Status,
  Trigger,
  Uuid,
} from '../types';

import { BranchesListFilter } from './BranchesListFilter';
import StaticStatusIcon from './StatusIcon/StaticStatusIcon';
import { Wrapper, subtleSelector } from './styled';
import { UsersEmptyState, UsersFilter } from './UsersFilter';

const STATUS_ITEMS = [
  {
    label: 'Successful',
    icon: 'success',
    value: ['PASSED', 'SUCCESSFUL'],
  },
  {
    label: 'Failed',
    icon: 'failed',
    value: ['FAILED', 'ERROR'],
  },
  {
    label: 'In progress',
    icon: 'building',
    value: ['PENDING', 'BUILDING', 'IN_PROGRESS'],
  },
  {
    label: 'Paused',
    icon: 'paused',
    value: ['PAUSED'],
  },
  {
    label: 'Halted',
    icon: 'halted',
    value: ['HALTED'],
  },
  {
    label: 'Stopped',
    icon: 'stopped',
    value: ['STOPPED', 'SKIPPED'],
  },
];
const TRIGGER_TYPE_ITEMS = [
  { label: 'Push', value: 'Push' },
  { label: 'Rerun / Manual', value: 'Manual' },
  { label: 'Scheduled', value: 'Scheduled' },
];
const formatPipelineTypeOption = (pipelineType: PipelineType) =>
  pipelineType.pattern
    ? {
        label: `${pipelineType.type}: ${pipelineType.pattern}`,
        value: `${pipelineType.type}: ${pipelineType.pattern}`,
      }
    : {
        label: `${pipelineType.type}`,
        value: `${pipelineType.type}`,
      };

const Filters = ({
  users,
  url,
  onUpdateFilter,
  pipelineDefinitions,
  pipelineDefinitionsLoading,
  getBranches,
  showUsersFilter,
  branchValue,
  onBranchChange,
}: FiltersProps) => {
  const getFilterValue = useCallback(
    (type: keyof PipelineFilter) => {
      const urlFilters = parseFilterPathParam(url);
      const urlHasFilters = Object.values(urlFilters).some(
        value => value !== null
      );

      if (urlHasFilters) return urlFilters[type];

      return null;
    },
    [url]
  );

  const getSelectedStatusItem = (
    selectedStatus: string[] | null | undefined
  ) => {
    const statusItem = STATUS_ITEMS.find(status =>
      isEqual(status.value, selectedStatus)
    );
    if (!statusItem) return null;

    return {
      label: (
        <p>
          <StaticStatusIcon
            iconName={statusItem.icon}
            iconText={statusItem.label}
          />
        </p>
      ),
      value: statusItem.value,
    };
  };

  const getSelectedTriggerTypeItem = (
    selectedTriggerType: string | null | undefined
  ) => {
    const triggerTypeItem = TRIGGER_TYPE_ITEMS.find(triggerType =>
      isEqual(triggerType.value, selectedTriggerType)
    );
    if (!triggerTypeItem) return null;

    return {
      label: triggerTypeItem.label,
      value: triggerTypeItem.value,
    };
  };

  const [selectedPipelineTypeItem, setSelectedPipelineTypeItem] =
    useState<any>(null);

  useEffect(() => {
    const selectedPipelineTypeItemVal = (pipelineDefinitions as any)?.filter(
      (pipelineType: PipelineType) =>
        isEqual(
          formatPipelineTypeOption(pipelineType).value,
          getFilterValue('selectedPipelineType')
        )
    )[0];
    setSelectedPipelineTypeItem(
      selectedPipelineTypeItemVal
        ? formatPipelineTypeOption(selectedPipelineTypeItemVal)
        : null
    );
  }, [pipelineDefinitions, getFilterValue]);

  const updateFilter = (
    type: keyof PipelineFilter,
    value: PipelineFilter[keyof PipelineFilter]
  ) => {
    const updatedFilters = {
      selectedStatus: getFilterValue(
        'selectedStatus'
      ) as PipelineFilter['selectedStatus'],
      selectedTriggerType: getFilterValue(
        'selectedTriggerType'
      ) as PipelineFilter['selectedTriggerType'],
      selectedPipelineType: getFilterValue(
        'selectedPipelineType'
      ) as PipelineFilter['selectedPipelineType'],
      uuid: getFilterValue('uuid') as PipelineFilter['uuid'],
      [type]: value || null,
    };
    onUpdateFilter(updatedFilters);
  };

  const getStatusItems = () =>
    STATUS_ITEMS.map((x: Status) => ({
      label: (
        <p>
          <StaticStatusIcon iconName={x.icon} iconText={x.label} />
        </p>
      ),
      value: x.value,
    }));

  const getTriggerTypeItems = () =>
    TRIGGER_TYPE_ITEMS.map((triggerType: Trigger) => ({
      label: triggerType.label,
      value: triggerType.value,
    }));

  const getPipelineTypeItems = () => {
    if (pipelineDefinitions) {
      return pipelineDefinitions.map(formatPipelineTypeOption);
    }
    return undefined;
  };

  const onBranchFilterChange = (item: any) => {
    updateFilter('selectedPipelineType', null);
    onBranchChange(item);
  };

  const onStatusChange = (item: any) => {
    updateFilter('selectedStatus', item?.value);
  };

  const onTriggerTypeChange = (item: any) => {
    updateFilter('selectedTriggerType', item?.value);
  };

  const onPipelineTypeChange = (item: any) => {
    updateFilter('selectedPipelineType', item?.value);
  };

  const onUserAvatarClick = (userUuid: Uuid) => {
    const isSameUser = getFilterValue('uuid') === userUuid;
    const toggledUserUuid = isSameUser ? null : userUuid;
    updateFilter('uuid', toggledUserUuid);
  };

  const additionalStyles = () => ({
    control: (css: CSSProperties, state: StateProps) => ({
      ...css,
      ...subtleSelector(css, state),
      height: 30,
      width: 165,
    }),
    dropdownIndicator: (css: CSSProperties) => ({
      ...css,
      padding: `${token('space.100')} ${token('space.100')} ${token(
        'space.100'
      )} 0px`,
    }),
    clearIndicator: (css: CSSProperties) => ({
      ...css,
      padding: `${token('space.100')} 0px ${token('space.100')} ${token(
        'space.100'
      )}`,
    }),
  });

  return (
    <Wrapper>
      {showUsersFilter &&
        (users.length ? (
          <UsersFilter
            selectedUser={getFilterValue('uuid') as PipelineFilter['uuid']}
            users={users}
            onUserAvatarClick={onUserAvatarClick}
          />
        ) : (
          <UsersEmptyState />
        ))}
      <BranchesListFilter
        getBranches={getBranches}
        branchValue={branchValue}
        onBranchChange={onBranchFilterChange}
      />
      <Select
        value={selectedPipelineTypeItem}
        options={getPipelineTypeItems()}
        onChange={onPipelineTypeChange}
        name="pipelineType"
        placeholder="Pipeline type"
        isClearable
        isLoading={pipelineDefinitionsLoading}
        styles={additionalStyles() as StylesConfig}
      />
      <Select
        value={getSelectedStatusItem(
          getFilterValue('selectedStatus') as PipelineFilter['selectedStatus']
        )}
        options={getStatusItems()}
        onChange={onStatusChange}
        name="status"
        placeholder="Status"
        isClearable
        isSearchable={false}
        styles={additionalStyles() as StylesConfig}
      />
      <Select
        value={getSelectedTriggerTypeItem(
          getFilterValue(
            'selectedTriggerType'
          ) as PipelineFilter['selectedTriggerType']
        )}
        options={getTriggerTypeItems()}
        onChange={onTriggerTypeChange}
        name="triggerType"
        placeholder="Trigger type"
        isClearable
        isSearchable={false}
        styles={additionalStyles() as StylesConfig}
      />
    </Wrapper>
  );
};

export default Filters;
